import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  MenuTriggerItem,
} from '../../../CommonComponents/OPL/Menu/Menu';
import { ListItem } from '../../../CommonComponents/OPL/List/ListItem';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { NOT_SELECTED, SELECTED } from '../../TransactionsConstants';
import { ScrollbarBox } from '../../../CommonComponents/OPL/ScrollbarBox/ScrollbarBox';

enum LEVEL {
  parent = 'parent',
  children = 'children',
}

interface Items {
  label: string;
  value: string;
}

interface Options extends Items {
  items: Items[];
}

interface Props {
  options: Options[];
  selectedOption: string;
  setOption(option: string): void;
  canClear?: boolean;
  onOpen?: (isOpen: boolean) => void;
}

export const CategoriesMenu: React.FC<Props> = ({
  options,
  selectedOption,
  setOption,
  children,
  canClear = false,
  onOpen = () => {},
}) => {
  const [optionLevel, setOptionLevel] = useState<LEVEL | null>(null);

  useEffectOnce(() => {
    if (optionLevel === null && selectedOption !== '') {
      options.forEach(parent => {
        if (parent.label === selectedOption) {
          setOptionLevel(LEVEL.parent);
        } else {
          setOptionLevel(LEVEL.children);
        }
      });
    }
  });

  const setOnExpenseCategoryFilterChange = (option, level) => {
    setOptionLevel(level);
    setOption(option);
  };

  return (
    <Menu onOpenChange={onOpen}>
      <MenuTrigger>{children}</MenuTrigger>

      <MenuContent>
        <ScrollbarBox css="max-height: 300px">
          {options.map(parent => {
            const isCurrentParentSelected = parent.label === selectedOption;

            const hasChildrenSelected =
              optionLevel === LEVEL.children &&
              parent.items.some(({ label }) => label === selectedOption);

            const items = [{ label: parent.label, value: parent.label }, ...parent.items];

            return (
              <Menu key={parent.label}>
                {parent.items.length === 0 ? (
                  <MenuItem
                    onSelect={() => {
                      setOnExpenseCategoryFilterChange(parent, LEVEL.parent);
                    }}
                  >
                    <ListItem
                      {...(isCurrentParentSelected && optionLevel === LEVEL.parent
                        ? SELECTED
                        : optionLevel !== null
                        ? NOT_SELECTED
                        : {})}
                    >
                      {parent.label}
                    </ListItem>
                  </MenuItem>
                ) : (
                  <>
                    <MenuTriggerItem>
                      <ListItem
                        {...(isCurrentParentSelected || hasChildrenSelected
                          ? SELECTED
                          : optionLevel !== null
                          ? NOT_SELECTED
                          : {})}
                      >
                        {parent.label}
                      </ListItem>
                    </MenuTriggerItem>

                    <MenuContent sideOffset={0} alignOffset={2}>
                      <ScrollbarBox css="max-height: 300px">
                        {items.map(child => {
                          const isCurrentChildSelected = child.label === selectedOption;

                          return (
                            <MenuItem
                              key={child.label}
                              onSelect={() =>
                                setOnExpenseCategoryFilterChange(child, LEVEL.children)
                              }
                            >
                              <ListItem
                                {...(isCurrentChildSelected && optionLevel === LEVEL.children
                                  ? SELECTED
                                  : isCurrentParentSelected || hasChildrenSelected
                                  ? NOT_SELECTED
                                  : {})}
                              >
                                {child.label}
                              </ListItem>
                            </MenuItem>
                          );
                        })}
                      </ScrollbarBox>
                    </MenuContent>
                  </>
                )}
              </Menu>
            );
          })}
        </ScrollbarBox>
        {selectedOption !== '' && canClear && (
          <Menu>
            <MenuSeparator />
            <MenuItem onSelect={() => setOnExpenseCategoryFilterChange({ value: '' }, null)}>
              <Box display="flex" justifyContent="flex-end">
                <Text color="primary.500">Clear</Text>
              </Box>
            </MenuItem>
          </Menu>
        )}
      </MenuContent>
    </Menu>
  );
};
